import React, { useContext, useState, useEffect } from "react";
import tw from "twin.macro";

import AppContext from "@components/AppContext";
import { boldHeader } from "@styles/globalStyles";
import Button from "@components/Button";
import errorMessages from "./ContactARep-errorMessages.js";
import ruleSet from "./ContactARep-ruleSet.js";
import states from "./ContactARep-states.js";
import validate from "@klickinc/util-validator";
import { useForm } from "react-hook-form";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import useSiteMetadata from "@hooks/use-site-metadata.js";
import { isEDMode, getUrlParameter, getUuid } from "@utils/utils";
import GTM from "@utils/GTM";
import ExitModal from "@components/ExitModal";

const getErrorMessage = (field_name, error) => {
  return errorMessages[field_name][error] || "";
};

const ContactARep = () => {
  const siteMetadata = useSiteMetadata();
  const { apiUrl } = siteMetadata;
  const { uuid } = useContext(AppContext);
  const [success, setSuccess] = useState(null);
  const [formStarted, setFormStarted] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onBlur",
    resolver: (data) => {
      let errors = {};
      errors = validate(ruleSet.signup.rules, data);
      return {
        values: Object.keys(errors).length ? {} : data,
        errors: errors,
      };
    },
  });

  const handleFormStart = () => {
    if (!formStarted) {
      GTM.formStart();
      setFormStarted(true);
    }
  };

  const handleFieldComplete = (fieldName, e) => {
    const fieldValue = e?.target?.value;
    if (fieldValue) GTM.fieldCompletion(fieldName, fieldValue);
  };

  const onSubmit = async (data) => {
    if (isSubmitting) {
      return;
    }

    const errors = validate(ruleSet.signup.rules, data);
    if (errors && Object.keys(errors).length > 0) {
      Object.keys(errors).forEach(function (key) {
        setError(key, {
          type: "Required",
        });
      });
    } else {
      try {
        // TODO: trim the values before sending to the backend
        data.recaptcha = await executeRecaptcha("formSubmit");
        data.form_code = "form_xerava_hcp_requestarep";
        data.uid = uuid || getUuid();

        const response = await fetch(`${apiUrl}/signup`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });

        const result = await response.json();

        if (response.ok && result) {
          if (result.success) {
            // Successfully submitted the form
            setSuccess(true);
            GTM.formSubmission();
          } else {
            if (
              result?.errors?.validation &&
              Object.keys(result.errors.validation).length > 0
            ) {
              Object.keys(result.errors.validation).forEach((key) => {
                setError(key, {
                  type: "manual",
                  message: result.errors.validation[key],
                });
              });
            }
          }
        } else {
          // Unable to submit the form
          setSuccess(false);
        }
      } catch (error) {
        // TODO: delete console log after testing
        console.error("Error:", error);
        setSuccess(false);

        return;
      }
    }
  };

  useEffect(() => {
    if (isEDMode) {
      const formSuccess = getUrlParameter("formSuccess");
      const formFailure = getUrlParameter("formFailure");
      const formFilled = getUrlParameter("formFilled");
      const formErrors = getUrlParameter("formErrors");

      const formField = {
        success: {
          "input[name='first_name']": "FirstName",
          "input[name='last_name']": "LastName",
          "input[name='email']": "loremipsum@email.com",
          "select[name='specialty']": "Other",
          "select[name='state']": "AL",
          "input[name='zip']": "00000",
          "input[name='npi']": "0000000000",
          "input[name='institution']": "Lorem ipsum",
        },
        failure: {
          "input[name='first_name']": "Firstname4!",
          "input[name='last_name']": "Lastname4!",
          "input[name='email']": "loremipsumemailcom",
          "select[name='specialty']": "",
          "select[name='state']": "",
          "input[name='zip']": "000000",
          "input[name='npi']": "00000",
          "input[name='institution']": "",
        },
      };

      if (formSuccess) {
        setSuccess(true);
      }

      if (formFailure) {
        setSuccess(false);
      }

      if (formFilled) {
        Object.keys(formField.success).forEach((key) => {
          document.querySelector(key).value = formField.success[key];
        });
      }

      if (formErrors === "empty") {
        document.querySelector("button[type='submit']").click();
      }

      if (formErrors === "nomatch") {
        Object.keys(formField.failure).forEach((key) => {
          document.querySelector(key).value = formField.failure[key];
          document.querySelector(key).focus();
          document.querySelector(key).blur();
        });
      }
    }
  }, []);

  useEffect(() => {
    const handleFieldErrorOnSubmit = () => {
      setTimeout(() => {
        const allErrorFields = document.querySelectorAll(
          "form [aria-invalid='true']",
        );
        const allErrorMessages = document.querySelectorAll(
          "form [aria-live='assertive']",
        );

        allErrorFields.forEach((input, i) => {
          GTM.fieldError(input.id, allErrorMessages[i].innerText);
        });
      }, 0);
    };

    document
      .querySelector('button[type="submit"]')
      .addEventListener("click", handleFieldErrorOnSubmit);

    return () => {
      if (document.querySelector('button[type="submit"]')) {
        document
          .querySelector('button[type="submit"]')
          .removeEventListener("click", handleFieldErrorOnSubmit);
      }
    };
  }, []);

  const divStyles = tw`relative`;
  const labelStyles = tw`block font-body font-normal pb-1 pt-5 text-base leading-[1.3] lg:(pt-10)`;
  const inputStyles = tw`text-magenta text-base font-bold h-[50px] w-full border-[1px] border-solid bg-periwinkle50 border-deepViolet01 !outline-0`;
  const linkStyles = tw`font-bold transition-all text-magenta underline`;
  const errorStyle = tw`block relative text-sm leading-[1.3] pl-6 mt-[0.3125rem] before:([clip-path: polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)] content-['!'] text-white bg-magenta px-1.5 absolute left-0 w-3.5 h-4) lg:(absolute)`;

  return (
    <>
      {success === null && (
        <section tw="px-4 pt-50 pb-6 lg:(pt-9 pb-10 px-11)">
          <h2 css={[boldHeader]}>
            Connect with a XERAVA
            <br tw="hidden lg:block" /> sales representative
          </h2>
          <p tw="text-center mt-5">
            If you are interested in receiving information about XERAVA or you'd
            like to <br tw="hidden lg:block" />
            speak with a sales representative, please fill out the form below.
          </p>

          <form
            onChange={handleFormStart}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            tw="text-left pt-2.5 lg:(pt-3.5)"
          >
            <div tw="lg:(grid grid-cols-2 gap-5)">
              <div css={divStyles}>
                <label
                  css={labelStyles}
                  id="label-first_name"
                  htmlFor="first_name"
                >
                  First Name
                </label>
                <input
                  css={inputStyles}
                  id="first_name"
                  name="first_name"
                  type="text"
                  placeholder=""
                  required
                  maxLength="100"
                  aria-required={true}
                  aria-invalid={errors.first_name && true}
                  aria-labelledby={
                    errors.first_name
                      ? "label-first_name error-first_name"
                      : null
                  }
                  {...register("first_name", {
                    setValueAs: (value) => value.trim(),
                  })}
                />
                {errors.first_name && (
                  <span
                    css={errorStyle}
                    id="error-first_name"
                    aria-live="assertive"
                  >
                    {getErrorMessage("first_name", errors.first_name[0])}
                  </span>
                )}
              </div>

              <div css={divStyles}>
                <label
                  css={labelStyles}
                  id="label-last_name"
                  htmlFor="last_name"
                >
                  Last Name
                </label>
                <input
                  css={inputStyles}
                  id="last_name"
                  name="last_name"
                  type="text"
                  placeholder=""
                  required
                  maxLength="100"
                  aria-required={true}
                  aria-invalid={errors.last_name && true}
                  aria-labelledby={
                    errors.last_name ? "label-last_name error-last_name" : null
                  }
                  {...register("last_name", {
                    setValueAs: (value) => value.trim(),
                  })}
                />
                {errors.last_name && (
                  <span
                    css={errorStyle}
                    id="error-last_name"
                    aria-live="assertive"
                  >
                    {getErrorMessage("last_name", errors.last_name[0])}
                  </span>
                )}
              </div>
            </div>

            <div css={divStyles}>
              <label css={labelStyles} id="label-email" htmlFor="email">
                Email
              </label>
              <input
                css={inputStyles}
                id="email"
                name="email"
                type="text"
                placeholder=""
                required
                maxLength="254"
                aria-required={true}
                aria-invalid={errors.email && true}
                aria-labelledby={
                  errors.email ? "label-email error-email" : null
                }
                {...register("email", {
                  setValueAs: (value) => value.trim(),
                })}
              />
              {errors.email && (
                <span css={errorStyle} id="error-email" aria-live="assertive">
                  {getErrorMessage("email", errors.email[0])}
                </span>
              )}
            </div>

            <div tw="lg:(flex gap-5)">
              <div css={[divStyles, tw`lg:w-1/2`]}>
                <label
                  css={labelStyles}
                  id="label-specialty"
                  htmlFor="specialty"
                >
                  Specialty
                </label>
                <select
                  css={inputStyles}
                  id="specialty"
                  name="specialty"
                  required
                  aria-required={true}
                  aria-invalid={errors.specialty && true}
                  aria-labelledby={
                    errors.specialty ? "label-specialty error-specialty" : null
                  }
                  {...register("specialty", {
                    onChange: (e) => handleFieldComplete("specialty", e),
                  })}
                >
                  <option value="" aria-label="Choose an option"></option>
                  <option value="Physician">Physician</option>
                  <option value="Pharmacist">Pharmacist</option>
                  <option value="NP/PA">NP/PA</option>
                  <option value="Hospital Leadership">
                    Hospital Leadership
                  </option>
                  <option value="Other">Other</option>
                </select>
                {errors.specialty && (
                  <span
                    css={errorStyle}
                    id="error-specialty"
                    aria-live="assertive"
                  >
                    {getErrorMessage("specialty", errors.specialty[0])}
                  </span>
                )}
              </div>

              <div tw="flex gap-[0.48rem] lg:(gap-5)">
                <div css={[divStyles, tw`w-1/2 lg:(w-[10.69rem])`]}>
                  <label css={labelStyles} id="label-state" htmlFor="state">
                    State
                  </label>
                  <select
                    css={inputStyles}
                    id="state"
                    name="state"
                    required
                    aria-required={true}
                    aria-invalid={errors.state && true}
                    aria-labelledby={
                      errors.state ? "label-state error-state" : null
                    }
                    {...register("state", {
                      onChange: (e) => handleFieldComplete("state", e),
                    })}
                  >
                    <option value="" aria-label="Choose an option"></option>
                    {states.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <span
                      css={[errorStyle, tw`lg:(w-[110%])`]}
                      id="error-state"
                      aria-live="assertive"
                    >
                      {getErrorMessage("state", errors.state[0])}
                    </span>
                  )}
                </div>
                <div css={[divStyles, tw`w-1/2 lg:(w-[13.19rem])`]}>
                  <label css={labelStyles} id="label-zip" htmlFor="zip">
                    ZIP
                  </label>
                  <input
                    css={inputStyles}
                    id="zip"
                    name="zip"
                    type="text"
                    placeholder=""
                    required
                    maxLength="5"
                    aria-required={true}
                    aria-invalid={errors.zip && true}
                    aria-labelledby={errors.zip ? "label-zip error-zip" : null}
                    {...register("zip", {
                      setValueAs: (value) => value.trim(),
                    })}
                  />
                  {errors.zip && (
                    <span css={errorStyle} id="error-zip" aria-live="assertive">
                      {getErrorMessage("zip", errors.zip[0])}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div css={divStyles}>
              <label css={labelStyles} id="label-npi" htmlFor="npi">
                NPI Number (Optional)
              </label>
              <input
                css={inputStyles}
                id="npi"
                name="npi"
                type="text"
                placeholder=""
                maxLength="10"
                aria-required={false}
                aria-invalid={errors.npi && true}
                aria-labelledby={errors.npi ? "label-npi error-npi" : null}
                {...register("npi", {
                  setValueAs: (value) => value.trim(),
                })}
              />
              {errors.npi && (
                <span css={errorStyle} id="error-npi" aria-live="assertive">
                  {getErrorMessage("npi", errors.npi[0])}
                </span>
              )}
            </div>

            <div css={divStyles}>
              <label
                css={labelStyles}
                id="label-institution"
                htmlFor="institution"
              >
                Institution Name (Optional)
              </label>
              <input
                css={inputStyles}
                id="institution"
                name="institution"
                type="text"
                placeholder=""
                maxLength="150"
                aria-required={false}
                aria-invalid={errors.institution && true}
                aria-labelledby={
                  errors.institution
                    ? "label-institution error-institution"
                    : null
                }
                {...register("institution", {
                  setValueAs: (value) => value.trim(),
                })}
              />
              {errors.institution && (
                <span
                  css={errorStyle}
                  id="error-institution"
                  aria-live="assertive"
                >
                  {getErrorMessage("institution", errors.institution[0])}
                </span>
              )}
            </div>

            <div tw="pt-10 text-base leading-[1.3] tracking-[0.01rem]">
              By submitting this form, I understand and agree to the Innoviva
              Specialty Therapeutics<sup>&trade;</sup>{" "}
              <ExitModal
                url="https://innovivaspecialtytherapeutics.com/privacy-policy/"
                triggerStyles={[linkStyles]}
                inline
              >
                Privacy Policy
              </ExitModal>{" "}
              and{" "}
              <ExitModal
                url="https://innovivaspecialtytherapeutics.com/terms-of-use/"
                triggerStyles={[linkStyles]}
                inline
              >
                Terms of Use
              </ExitModal>
              . I agree to be contacted by a XERAVA representative and to
              receive communications from Innoviva Specality Therapeutics
              <sup>&trade;</sup>.
            </div>

            <div tw="pt-4 text-base leading-[1.3] tracking-[0.01rem]">
              This site is protected by reCAPTCHA and the Google{" "}
              <ExitModal
                url="https://policies.google.com/privacy"
                triggerStyles={[linkStyles]}
                inline
              >
                Privacy Policy
              </ExitModal>{" "}
              and{" "}
              <ExitModal
                url="https://policies.google.com/terms"
                triggerStyles={[linkStyles]}
                inline
              >
                Terms of Service
              </ExitModal>{" "}
              apply.
            </div>

            <Button
              tw="block mx-auto mt-10 w-fit font-black tracking-[0.01131rem] bg-magenta pl-14 pr-16"
              onClick={() => {
                setSuccess(null);
              }}
              type="submit"
              disabled={isSubmitting}
            >
              Submit
            </Button>
          </form>
        </section>
      )}
      {success === true && (
        <div tw="text-center py-14 lg:py-24" data-kfs="contact-a-rep-success">
          <h2 tw="font-display font-bold uppercase text-4xl leading-[0.75] pb-5 lg:(text-5xl leading-[0.77])">
            Thank you for contacting us!
          </h2>
          <p>
            A sales representative will be
            <br tw="block lg:hidden" /> in touch with you soon.
          </p>
        </div>
      )}
      {success === false && (
        <div tw="text-center py-14 lg:py-16" data-kfs="contact-a-rep-error">
          <h2 tw="font-display font-bold uppercase text-4xl leading-[0.75] pb-5 lg:(text-5xl leading-[0.77])">
            SORRY!
          </h2>
          <p>There was an error submitting your&nbsp;information.</p>
          <Button
            tw="block mx-auto mt-[1.88rem] w-fit font-black tracking-[0.01131rem] bg-magenta pl-14 pr-16"
            onClick={() => {
              setSuccess(null);
            }}
          >
            GO BACK
          </Button>
        </div>
      )}
    </>
  );
};

export default ContactARep;
